import type { ApiError, UserPreferencesServiceList, WindowApi } from "../shared/types.js";
import { createCustomEvent } from "../internal/customEvent.js";
import type { AddServiceIdToFavoriteServiceIdListError, FavoriteServiceData } from "./types.js";
import {
  ADD_SERVICE_ID_TO_FAVORITE_SERVICE_ID_LIST_EVENT,
  ADD_SERVICE_ID_TO_FAVORITE_SERVICE_ID_LIST_ERROR_EVENT,
  SERVICE_LIST_ERROR_EVENT,
  SERVICE_LIST_UPDATED_EVENT,
} from "./types.js";

declare const window: WindowApi;

export const getCCSAvailable = (): boolean => {
  return !!window.AWSC?.CCS?.ccsAvailable;
};

export const setCCSIsLoading = (isLoading: boolean): void => {
  if (window.AWSC?.CCS) {
    window.AWSC.CCS.ccsIsLoading = isLoading;
  }
};

export const setCCSInitialError = (error: ApiError): void => {
  if (window.AWSC?.CCS) {
    window.AWSC.CCS.ccsInitialError = error;
  }
};

export const getCCSIsLoading = (): boolean => {
  return !!window.AWSC?.CCS?.ccsIsLoading;
};

export const getCCSInitialError = (): ApiError | undefined => {
  return window.AWSC?.CCS?.ccsInitialError;
};

export const setInitialUserServiceList = (serviceList: UserPreferencesServiceList) => {
  if (window.AWSC?.CCS) {
    window.AWSC.CCS.initialUserServiceList = serviceList;
  }
};

export const getInitialUserServiceListFromWindow = (): UserPreferencesServiceList | undefined => {
  return window.AWSC?.CCS?.initialUserServiceList;
};

export const updateRecentlyVisitedInitialUserServiceList = (
  recentlyVisitedServiceIdList: UserPreferencesServiceList["recentlyVisitedServiceIdList"]
) => {
  if (window.AWSC?.CCS?.initialUserServiceList) {
    window.AWSC.CCS.initialUserServiceList.recentlyVisitedServiceIdList = recentlyVisitedServiceIdList;
  }
};

export const updateFavoriteInitialUserServiceList = (
  favoriteServiceIdList: UserPreferencesServiceList["recentlyVisitedServiceIdList"]
) => {
  if (window.AWSC?.CCS?.initialUserServiceList) {
    window.AWSC.CCS.initialUserServiceList.favoriteServiceIdList = favoriteServiceIdList;
  }
};

export const updateInitialUserServiceListAndDispatch = ({
  favoriteServiceIdList,
  recentlyVisitedServiceIdList,
}: Partial<UserPreferencesServiceList>) => {
  if (favoriteServiceIdList && recentlyVisitedServiceIdList) {
    setInitialUserServiceList({
      favoriteServiceIdList,
      recentlyVisitedServiceIdList,
    });
  } else if (favoriteServiceIdList) {
    updateFavoriteInitialUserServiceList(favoriteServiceIdList);
  } else if (recentlyVisitedServiceIdList) {
    updateRecentlyVisitedInitialUserServiceList(recentlyVisitedServiceIdList);
  }
  const event = createCustomEvent(SERVICE_LIST_UPDATED_EVENT, {
    detail: {
      favoriteServiceIdList,
      recentlyVisitedServiceIdList,
    },
  });
  window.dispatchEvent(event);
};

export const updateServiceListErrorAndDispatch = (error: ApiError) => {
  setCCSInitialError(error);
  const event = createCustomEvent(SERVICE_LIST_ERROR_EVENT, {
    detail: error,
  });
  window.dispatchEvent(event);
};

export const listenToAddServiceIdToFavoriteServiceIdListEvent = (
  listener: ({ detail }: CustomEvent<FavoriteServiceData>) => void
) => {
  window.addEventListener(ADD_SERVICE_ID_TO_FAVORITE_SERVICE_ID_LIST_EVENT, listener as EventListener);
};

export const dispatchAddServiceIdToFavoriteServiceIdListErrorEvent = (
  error: AddServiceIdToFavoriteServiceIdListError
) => {
  const event = createCustomEvent(ADD_SERVICE_ID_TO_FAVORITE_SERVICE_ID_LIST_ERROR_EVENT, {
    detail: { error },
  });
  window.dispatchEvent(event);
};

export const setUserSettingsPromise = (userSettingsPromise: Promise<unknown>) => {
  if (window.AWSC?.CCS) {
    window.AWSC.CCS.userSettingsPromise = userSettingsPromise;
  }
};

export const getUserSettingsPromise = () => {
  return window.AWSC?.CCS?.userSettingsPromise;
};
